import { RequestAction } from '@redux-requests/core';
import { gql } from '@redux-requests/graphql';
import { t, addFallbackTranslations } from 'utils';
import { AppsConstants } from './constants';
import { transformHookResponse, transformRolesResponse } from './responseTransforms';

export interface IAppPlan {
  name: string;
  description: string;
  monthlyPrice: number;
  usageUnitPrice: number;
  usageMinUnitPrice: number;
  usageMaxUnitPrice: number;
  includedUnits: number;
  activationFeePrice: number;
}
export interface IAppCountry {
  country: string;
  currencyCode: string;
  activationFeePrice: number;
  hasPayment: boolean;
  plans: IAppPlan[];
  trialUnits: number;
  trialDays: number;
  unitDescription: string;
  unitDescriptionPlural: string;
  priceDescription: string;
}

export interface IAppLanguage {
  language: string;
  name?: string;
  description?: string;
  benefits?: string;
  functionality?: string;
  tutorialUrl?: string;
  privacyUrl?: string;
  gallery?: [string];
}

export interface IApp {
  id: string;
  name: string;
  isOnlyBackEnd: string;
  isPublic: string;
  roles: [];
  languages: IAppLanguage[];
  categories: string[];
  allowedCountries: IAppCountry[];
  plan: string;
}

export interface IAPIError {
  data: { errors: Array<{ extensions: { code: string } }> };
}

export const createNewApp = ({
  name,
  isOnlyBackEnd,
  isPublic,
  roles,
  categories,
  languages,
  allowedCountries,
  plan,
}: IApp): RequestAction => ({
  type: AppsConstants.APP_CREATE,
  request: {
    query: gql`
      mutation(
        $name: String
        $isPublic: Boolean
        $isOnlyBackEnd: Boolean
        $roles: [AppRolePayload]
        $languages: [AppLanguageDataPayload]
        $categories: [String]
        $allowedCountries: [AppCountryPayload]
        $plan: CompanyPlan
      ) {
        saveApp(
          app: {
            name: $name
            isPublic: $isPublic
            isOnlyBackEnd: $isOnlyBackEnd
            roles: $roles
            languages: $languages
            categories: $categories
            allowedCountries: $allowedCountries
            plan: $plan
            version: "1.0.0"
          }
        ) {
          id
          externalId
          name
        }
      }
    `,
    variables: {
      name,
      isOnlyBackEnd,
      isPublic,
      roles,
      languages,
      categories,
      allowedCountries,
      plan,
    },
  },
  meta: {
    getData: ({ saveApp }) => saveApp,
    // getData: ({ saveApp }) => {
    //   const { data = {}, error } = saveApp;
    //   const { data: errorData = {} } = error || {};
    //   const { errors = [] } = errorData || {};
    //   const errorCodes = errors.map(
    //     (item: { extensions: { code: string } }) => item.extensions.code
    //   );
    //   if (!data) return { errors: errorCodes };
    //   return { data };
    // },
  },
});

export const getAllAppCategories = (): RequestAction => ({
  type: AppsConstants.APP_CATEGORIES,
  request: {
    query: gql`
      {
        getAllAppCategories {
          code
          translationKey
        }
      }
    `,
  },
  meta: {
    getData: ({ getAllAppCategories }) =>
      getAllAppCategories.map(
        ({ code, translationKey }: { code: string; translationKey: string }) => ({
          value: code,
          label: translationKey,
        })
      ),
  },
});

export interface ICountry {
  code: string;
  label: string;
  translationKey: string;
  languages: string[];
  overwriteCurrencyCode: string;
  versionOnePrices: { overwriteCurrencyCode: string };
}

export const getAllCountries = (): RequestAction => ({
  type: AppsConstants.APP_COUNTRIES,
  request: {
    query: gql`
      {
        getAllCountries {
          code
          label
          translationKey
          languages
          overwriteCurrencyCode
          versionOnePrices {
            overwriteCurrencyCode
          }
        }
      }
    `,
  },
  meta: {
    getData: ({ getAllCountries }: { getAllCountries: ICountry[] }) => {
      // use the API country name when its translation key is missing in Phrase
      addFallbackTranslations(
        getAllCountries.reduce<{ [key: string]: string }>(
          (acc, { translationKey, label }) => ({ ...acc, [translationKey]: label }),
          {}
        )
      );
      return getAllCountries
        .sort((c1, c2) => (c1.label < c2.label ? -1 : 1))
        .map((country: ICountry) => ({
          ...country,
          value: country.code,
          label: t(country.translationKey),
        }));
    },
  },
});

export interface ILocale {
  code: string;
  language: string;
  languageCode: string;
  languageName: string;
}

export const getAllLocales = (): RequestAction => ({
  type: AppsConstants.APP_LOCALES,
  request: {
    query: gql`
      {
        getAllLocales {
          code
          language {
            code
            translationKey
            label
          }
          languageCode
          languageName
        }
      }
    `,
  },
  meta: {
    getData: ({ getAllLocales }: { getAllLocales: ILocale[] }) =>
      getAllLocales.sort((c1, c2) => (c1.languageName < c2.languageName ? -1 : 1)),
  },
});

export const listPrivateApp = (appId: string, secret: string): RequestAction => ({
  type: AppsConstants.APP_LIST_PRIVATE,
  request: {
    query: gql`
      mutation($appId: ID!, $secret: ID!) {
        listPrivateApp(appId: $appId, secret: $secret) {
          id
          name
          isEnterprise
        }
      }
    `,
    variables: { appId, secret },
  },
  meta: {
    requestKey: appId,
    mutations: {
      [AppsConstants.APP + appId]: (data, { listPrivateApp }) => {
        if (!listPrivateApp) return data;
        const { isEnterprise } = listPrivateApp || {};
        const newCompanies = [...data.companies];
        const newEnterprises = [...data.enterprises];
        if (isEnterprise) newEnterprises.push(listPrivateApp);
        else newCompanies.push(listPrivateApp);
        return {
          ...data,
          companies: newCompanies,
          enterprises: newEnterprises,
        };
      },
    },
  },
});

type UnlistPrivateApp = (
  appId: string,
  companyId: string | null,
  enterpriseId: string | null
) => RequestAction;

export const unlistPrivateApp: UnlistPrivateApp = (appId, companyId, enterpriseId) => ({
  type: AppsConstants.APP_UNLIST_PRIVATE,
  request: {
    query: gql`
      mutation($appId: ID!, $companyId: ID, $enterpriseId: ID) {
        unlistPrivateApp(appId: $appId, companyId: $companyId, enterpriseId: $enterpriseId)
      }
    `,
    variables: { appId, companyId, enterpriseId },
  },
  meta: {
    requestKey: appId,
    getData: ({ unlistPrivateApp }) => unlistPrivateApp,
  },
});

export const getApps = (): RequestAction => ({
  type: AppsConstants.APPS,
  request: {
    query: gql`
      {
        getApps {
          id
          name
          version
          plan
          imageUrl
          isPublic
          status
          isOnlyBackEnd
          installmentsCount
          categories
          allowedCountries {
            hasPayment
          }
        }
      }
    `,
  },
  meta: {
    getData: ({ getApps }) => getApps,
  },
});

export const getApp = (id: string): RequestAction => ({
  type: AppsConstants.APP,
  request: {
    query: gql`
      query($id: ID!) {
        getApp(id: $id) {
          id
          name
          version
          plan
          imageUrl
          isPublic
          status
          installmentsCount
          categories
          allowFullscreenView
          abbreviatedName
          iconType
          iconTextColor
          iconImageUrl
          iconBackgroundColor
          iconFontAwesomeName
          description
          allowedCountries {
            country
            currencyCode
            activationFeePrice
            hasPayment
            plans {
              name
              monthlyPrice
              description
              usageUnitPrice
              usageMinUnitPrice
              usageMaxUnitPrice
              includedUnits
            }
            trialUnits
            trialDays
            unitDescription
            unitDescriptionPlural
            priceDescription
          }
          hookUrl
          hookSecret
          roles {
            name
            types
          }
          companies {
            id
            name
            enterprise {
              id
              name
            }
          }
          enterprises {
            id
            name
          }
          hooks {
            id
            name
            url
            isActive
            secret
            secretHeader
            events {
              name
              types
            }
          }
          statusHistory {
            date
            admin
            developer
            note
            from
            to
          }
          languages {
            language
            name
            description
            benefits
            functionality
          }
          gallery
          privacyUrl
          tutorialUrl
        }
      }
    `,
    variables: { id },
  },
  meta: {
    requestKey: id,
    getData: ({ getApp }) => {
      if (!getApp) return getApp;
      const { hooks, roles, enterprises = [], companies = [] } = getApp;
      // make them the same as companies for easy mapping
      const transformedCompanies = companies.map((company: any) => ({
        ...company,
        isEnterprise: false,
      }));
      const transformedEnterprises = enterprises.map((enterprise: any) => ({
        ...enterprise,
        isEnterprise: true,
      }));
      const transformedHooks = hooks.map((hook: any) => transformHookResponse(hook));
      return {
        ...getApp,
        companies: transformedCompanies,
        enterprises: transformedEnterprises,
        hooks: transformedHooks,
        roles: transformRolesResponse(roles),
        isFetched: true,
        hasMoreLogs: true,
      };
    },
  },
});

export const deleteApp = (id: string): RequestAction => ({
  type: AppsConstants.APP_DELETE,
  request: {
    query: gql`
      mutation($id: ID!) {
        deleteApp(id: $id)
      }
    `,
    variables: { id },
  },
  meta: {
    getData: ({ deleteApp }) => deleteApp,
  },
});

export interface ICreateNewVersion {
  id: string;
  roles: any[];
  version: string;
}

export const createNewVersion = ({ id, roles, version }: ICreateNewVersion): RequestAction => ({
  type: AppsConstants.APP_CREATE_NEW_VERSION,
  request: {
    query: gql`
      mutation($id: ID, $roles: [AppRolePayload], $version: String) {
        saveApp(app: { id: $id, roles: $roles, version: $version }) {
          id
          version
          roles {
            name
            types
          }
        }
      }
    `,
    variables: {
      id,
      roles: roles.map(({ name, types }) => ({ name, types })),
      version,
    },
  },
  meta: {
    requestKey: id,
    mutations: {
      [AppsConstants.APP + id]: (data, { saveApp }) => {
        const { roles } = saveApp;
        return { ...data, ...saveApp, roles: transformRolesResponse(roles) };
      },
    },
  },
});

export const generateAppSecret = (id: string): RequestAction => ({
  type: AppsConstants.GENERATE_SECRET,
  request: {
    query: gql`
      mutation($id: ID!) {
        generateAppSecret(appId: $id)
      }
    `,
    variables: { id },
  },
  meta: {
    requestKey: id,
    mutations: {
      [AppsConstants.APP + id]: (data, { generateAppSecret }) => ({
        ...data,
        sandboxSecret: generateAppSecret,
      }),
    },
  },
});

export interface IChangeAppInfo {
  id: string;
  imageFile: string;
  description: string;
  hookUrl: string | null;
  hookSecret: string | null;
  allowFullscreenView: boolean;
  iconBackgroundColor: string;
  iconTextColor: string;
  iconFontAwesomeName: string;
  iconImageUrl: string | null;
  abbreviatedName: string;
}

export const changeAppInfo = ({ id, ...values }: IChangeAppInfo): RequestAction => ({
  type: AppsConstants.APP_EDIT,
  request: {
    query: gql`
      mutation(
        $id: ID
        $imageFile: String
        $description: String
        $hookUrl: String
        $hookSecret: String
        $allowFullscreenView: Boolean
        $iconBackgroundColor: HTMLColor
        $iconTextColor: HTMLColor
        $iconFontAwesomeName: String
        $iconImageUrl: String
        $abbreviatedName: String
        $iconType: AppIconType
      ) {
        saveApp(
          app: {
            id: $id
            imageFile: $imageFile
            description: $description
            hookUrl: $hookUrl
            hookSecret: $hookSecret
            allowFullscreenView: $allowFullscreenView
            iconBackgroundColor: $iconBackgroundColor
            iconTextColor: $iconTextColor
            iconFontAwesomeName: $iconFontAwesomeName
            iconImageUrl: $iconImageUrl
            abbreviatedName: $abbreviatedName
            iconType: $iconType
          }
        ) {
          id
          iconType
          iconTextColor
          iconImageUrl
          iconFontAwesomeName
          iconBackgroundColor
          imageUrl
          allowFullscreenView
          description
          abbreviatedName
        }
      }
    `,
    variables: { id, ...values },
  },
  meta: {
    mutations: {
      [AppsConstants.APP + id]: (data, { saveApp }) => ({ ...data, ...saveApp }),
    },
  },
});
